import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Calculator } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import {
  COMMON_DEVICES,
  getMaxZs,
  isValidCombination,
} from './zsCommonDevicesData';

const ERROR_DIALOG_TITLE = 'Invalid protective device type and rating combination';
const ERROR_DIALOG_MESSAGE =
  'The device rating (A) of the selected protective device does not exist, select a different protective device type.';

function InvalidCombinationDialog({ open, onOpenChange }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl rounded-lg overflow-hidden">
          <div className="bg-green-600 dark:bg-green-700 px-6 py-4">
            <Dialog.Title className="text-lg font-semibold text-white">
              {ERROR_DIALOG_TITLE}
            </Dialog.Title>
          </div>
          <div className="px-6 py-5">
            <p className="text-gray-700 dark:text-gray-300">
              {ERROR_DIALOG_MESSAGE}
            </p>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <Button onClick={() => onOpenChange(false)}>OK</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SelectableRow({ label, isSelected, onClick, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors ${className} ${
        isSelected
          ? 'bg-blue-600 dark:bg-blue-600 text-white'
          : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
    >
      {label}
    </button>
  );
}

function ZsCommonDevicesSelection({ onCalculate, isMobile }) {
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedRating, setSelectedRating] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const ratings = selectedDevice
    ? COMMON_DEVICES.find((d) => d.id === selectedDevice.id)?.ratings ?? []
    : [];

  const handleCalculate = () => {
    if (!selectedDevice || selectedRating == null) return;
    const valid = isValidCombination(selectedDevice.id, selectedRating);
    if (!valid) {
      setShowErrorDialog(true);
      return;
    }
    onCalculate(selectedDevice, selectedRating);
  };

  const canCalculate = selectedDevice != null && selectedRating != null;

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 flex-1 min-h-0">
        {/* Left column - Device */}
        <div className="flex-1 min-w-0 flex flex-col border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Device
            </h2>
          </div>
          <div className="overflow-y-auto flex-1 max-h-[280px] sm:max-h-[360px]">
            {COMMON_DEVICES.map((d) => (
              <SelectableRow
                key={d.id}
                label={d.label}
                isSelected={selectedDevice?.id === d.id}
                onClick={() => {
                  setSelectedDevice(d);
                  setSelectedRating(null);
                }}
              />
            ))}
          </div>
        </div>

        {/* Right column - Rating (A) */}
        <div className="flex-1 min-w-0 flex flex-col border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Rating (A)
            </h2>
          </div>
          <div className="overflow-y-auto flex-1 max-h-[280px] sm:max-h-[360px]">
            {selectedDevice ? (
              ratings.map((r) => (
                <SelectableRow
                  key={r}
                  label={String(r)}
                  isSelected={selectedRating === r}
                  onClick={() => setSelectedRating(r)}
                  className="text-right"
                />
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                Select a device first
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Calculate button - fixed at bottom on mobile */}
      <div className="mt-4 flex-shrink-0">
        <Button
          onClick={handleCalculate}
          disabled={!canCalculate}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Calculator className="h-5 w-5" />
          Calculate
        </Button>
      </div>

      <InvalidCombinationDialog
        open={showErrorDialog}
        onOpenChange={setShowErrorDialog}
      />
    </>
  );
}

function MaxZsResults({ device, rating, onBack, isMobile }) {
  const result = getMaxZs(device.id, rating);
  const backPath = isMobile
    ? '/tenant/mobile/calculators/max-zs-values/zs-common-devices'
    : '/tenant/tools/calculators/max-zs-values/zs-common-devices';

  if (!result) {
    return (
      <div className="text-gray-600 dark:text-gray-400">
        No data available for this combination.
      </div>
    );
  }

  const { zs100, zs80, times } = result;

  return (
    <div className={`space-y-4 ${isMobile ? 'pb-8' : ''}`}>
      {/* Selected device bar - red */}
      <div className="flex items-center justify-between bg-red-600 dark:bg-red-700 px-4 py-3 rounded-lg text-white">
        <span className="font-medium">{device.label}</span>
        <span className="font-semibold">{rating} A</span>
      </div>

      {/* 100% Values (BS 7671) */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-green-600 dark:bg-green-700 px-4 py-2">
          <h3 className="text-sm font-semibold text-white">
            100% Values (BS 7671)
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                  Disconnection Time
                </th>
                <th className="px-4 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                  Max Zs
                </th>
              </tr>
            </thead>
            <tbody>
              {times.map((t) => (
                <tr
                  key={t}
                  className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50"
                >
                  <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                    {t}
                  </td>
                  <td className="px-4 py-2 text-right text-blue-600 dark:text-blue-400 font-medium">
                    {zs100}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 80% Values (On-Site Guide) */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-green-600 dark:bg-green-700 px-4 py-2">
          <h3 className="text-sm font-semibold text-white">
            80% Values (On-Site Guide)
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300">
                  Disconnection Time
                </th>
                <th className="px-4 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                  Max Zs
                </th>
              </tr>
            </thead>
            <tbody>
              {times.map((t) => (
                <tr
                  key={t}
                  className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50"
                >
                  <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                    {t}
                  </td>
                  <td className="px-4 py-2 text-right text-blue-600 dark:text-blue-400 font-medium">
                    {zs80}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results information */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
        <p>Disconnection times are in seconds (s).</p>
        <p className="font-medium">Results Information:</p>
        <p>
          <strong>Manu. Specific:</strong> Zs values can vary by manufacturer.
          Refer to manufacturer data for specific devices.
        </p>
        <p>
          <strong>N/A:</strong> Indicates the device may not trip or the
          combination does not exist in the standard.
        </p>
        <p>
          <strong>80% Values:</strong> Temperature-adjusted values for measured
          Zs on site, rounded to two decimal places from the 100% values.
        </p>
      </div>

      <Button
        variant="outline"
        onClick={onBack}
        className="w-full"
      >
        Calculate Another
      </Button>
    </div>
  );
}

export default function ZsCommonDevices() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');
  const maxZsBasePath = isMobile
    ? '/tenant/mobile/calculators/max-zs-values'
    : '/tenant/tools/calculators/max-zs-values';
  const backPath = maxZsBasePath;
  const homePath = isMobile ? '/tenant/mobile' : '/tenant/dashboard';

  const [result, setResult] = useState(null);

  const content = (
    <div className="max-w-2xl mx-auto flex flex-col min-h-0">
      {/* Header bar - green */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-4 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg flex-shrink-0">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">
          {result ? 'Max Zs Results' : 'Common Devices'}
        </h1>
        <Link
          to={homePath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      {result ? (
        <MaxZsResults
          device={result.device}
          rating={result.rating}
          onBack={() => setResult(null)}
          isMobile={isMobile}
        />
      ) : (
        <ZsCommonDevicesSelection
          onCalculate={(device, rating) => setResult({ device, rating })}
          isMobile={isMobile}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 flex flex-col">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Zs Values : Common Devices
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Select a protective device and rating to calculate maximum Zs values
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
